import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Empty, Input } from 'antd';
import { useInterval } from 'ahooks';
import { PageEnum } from '@/enums/pageEnum';
import useMessageRedux from '@/store/message-redux';
import {
  dismissAiCall,
  getAiCall,
  getAiCallPage,
  getAiCallPending,
  submitAiCall,
  type AiCallVo,
  type BulkyRemovalFormData,
} from '@/facilities/modules/aiCall';
import CallList, { type CallListTab } from './CallList';
import BulkyRemovalForm from './BulkyRemovalForm';
import CallTranscript from './CallTranscript';
import { isLive } from './liveUtils';
import { useBrowserPhone } from './browserPhone';
import { BrowserCallPanel } from './BrowserCallPanel';
import { getAiHotlineSettings } from '@/facilities/modules/aiHotlineSettings';
import { toMillis } from './utils';

const POLL_MS = 10000;
/** Settings › AI Hotline (take over answering) is re-read this often */
const SETTINGS_POLL_MS = 30000;
/** while a call is connected the list and the open call refresh much faster */
const LIVE_LIST_POLL_MS = 3000;
const LIVE_DETAIL_POLL_MS = 1500;

/** how long after a transfer we keep refreshing for the transcript of the human leg */
const TRANSCRIPT_WAIT_MS = 10 * 60 * 1000;

/**
 * A transferred call gets its caller/officer transcript 10-30 s after hang-up: keep refreshing
 * until it arrives (or clearly never will: no answer, failed, or too long ago).
 */
const awaitingTranscript = (call: AiCallVo | null) => {
  const tr = call?.transfer;
  if (!call || !tr?.to || call.status !== 'PENDING_REVIEW') return false;
  if (tr.transcript === 'done' || tr.transcript === 'failed') return false;
  if (tr.status !== 'dialing' && tr.status !== 'answered') return false;
  const since = toMillis(tr.requestedAt);
  return !since || Date.now() - since < TRANSCRIPT_WAIT_MS;
};

const inbox = (status: AiCallVo['status']) =>
  status === 'PENDING_REVIEW' || status === 'IN_PROGRESS';

/** cheap change detection so live polling does not re-render (and re-animate) identical data */
const sameCall = (a: AiCallVo | null, b: AiCallVo | null) =>
  !!a &&
  !!b &&
  a.id === b.id &&
  a.status === b.status &&
  a.updatedAt === b.updatedAt &&
  a.transfer?.status === b.transfer?.status &&
  (a.dialogues?.length || 0) === (b.dialogues?.length || 0) &&
  JSON.stringify(a.formData) === JSON.stringify(b.formData);

export default function AiHotline() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { message, modal } = useMessageRedux();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = Number(searchParams.get('id')) || null;

  const [tab, setTab] = useState<CallListTab>('PENDING_REVIEW');
  const [calls, setCalls] = useState<AiCallVo[]>([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [listLoading, setListLoading] = useState(true);
  const [detail, setDetail] = useState<AiCallVo | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [takeoverTarget, setTakeoverTarget] = useState<'phone' | 'browser'>('phone');
  const browserPhone = useBrowserPhone(takeoverTarget === 'browser');
  const dismissNote = useRef('');
  const tabRef = useRef(tab);
  tabRef.current = tab;
  const selectedIdRef = useRef(selectedId);
  selectedIdRef.current = selectedId;
  const detailRef = useRef(detail);
  detailRef.current = detail;

  const loadList = useCallback(async () => {
    try {
      const [res, pending] = await Promise.all([
        getAiCallPage({
          page: 1,
          size: 50,
          status: tab === 'PENDING_REVIEW' ? 'PENDING_REVIEW' : undefined,
        }),
        getAiCallPending(),
      ]);
      // the tab changed while the request was in flight: drop the stale result
      if (tabRef.current !== tab) return;
      const content: AiCallVo[] = res.data?.body?.content || [];
      const live: AiCallVo[] = pending?.data?.body?.live || [];
      setCalls(
        tab === 'PENDING_REVIEW'
          ? [...live, ...content.filter((c) => c.status === 'PENDING_REVIEW')]
          : content.filter((c) => !inbox(c.status)),
      );
      setPendingCount(pending?.data?.body?.count ?? 0);
    } catch (error: unknown) {
      message.error(String((error as { detail?: string })?.detail || error));
    } finally {
      setListLoading(false);
    }
  }, [tab, message]);

  const loadDetail = useCallback(
    async (id: number) => {
      setDetailLoading(true);
      try {
        const res = await getAiCall(id);
        const call: AiCallVo | null = res.data?.body || null;
        setDetail(call);
        // the list follows the loaded call: after submit / dismiss it moves to
        // "Reviewed", and a deep link (notification, reload) may point at either tab
        if (call && selectedIdRef.current === id) {
          setTab(inbox(call.status) ? 'PENDING_REVIEW' : 'REVIEWED');
        }
      } catch (error: unknown) {
        message.error(String((error as { detail?: string })?.detail || error));
        setDetail(null);
      } finally {
        setDetailLoading(false);
      }
    },
    [message],
  );

  // silent refresh of the open call while it is live or still waiting for the transcript of a
  // transfer (no spinner, no flicker)
  const refreshLive = useCallback(async () => {
    const current = detailRef.current;
    if (!current || !(isLive(current) || awaitingTranscript(current))) return;
    try {
      const res = await getAiCall(current.id);
      const next: AiCallVo | null = res.data?.body || null;
      if (!next || selectedIdRef.current !== current.id) return;
      if (sameCall(current, next)) return;
      setDetail(next);
      if (isLive(current) && !isLive(next)) {
        message.success(t('hotline_callEnded'));
        loadList();
      }
    } catch {
      /* transient: the next tick retries */
    }
  }, [message, t, loadList]);

  useEffect(() => {
    setListLoading(true);
    loadList();
  }, [loadList]);

  const loadSettings = useCallback(async () => {
    try {
      const res = await getAiHotlineSettings();
      setTakeoverTarget(
        res.data?.body?.takeoverTarget === 'browser' ? 'browser' : 'phone',
      );
    } catch {
      /* keep the last known value */
    }
  }, []);
  useEffect(() => {
    loadSettings();
  }, [loadSettings]);
  useInterval(loadSettings, SETTINGS_POLL_MS);

  const anyLive = useMemo(
    () => isLive(detail) || calls.some((c) => isLive(c)),
    [detail, calls],
  );
  useInterval(loadList, anyLive ? LIVE_LIST_POLL_MS : POLL_MS);
  useInterval(
    refreshLive,
    isLive(detail)
      ? LIVE_DETAIL_POLL_MS
      : awaitingTranscript(detail)
        ? LIVE_LIST_POLL_MS
        : undefined,
  );

  useEffect(() => {
    if (selectedId) {
      loadDetail(selectedId);
    } else {
      setDetail(null);
    }
  }, [selectedId, loadDetail]);

  // auto-select when nothing is selected: a live call first, else the newest pending one
  // (only inbox calls: right after a tab switch `calls` still holds the other list)
  useEffect(() => {
    if (selectedId || tab !== 'PENDING_REVIEW') return;
    const first = calls.find((c) => isLive(c)) || calls.find((c) => inbox(c.status));
    if (first) setSearchParams({ id: String(first.id) }, { replace: true });
  }, [calls, selectedId, tab, setSearchParams]);

  const select = (call: AiCallVo) => setSearchParams({ id: String(call.id) });

  const onTabChange = (next: CallListTab) => {
    if (next === tab) return;
    setTab(next);
    setCalls([]);
    setListLoading(true);
    setSearchParams({});
  };

  const handleSubmit = async (formData: BulkyRemovalFormData) => {
    if (!detail) return;
    setSubmitting(true);
    try {
      const res = await submitAiCall(detail.id, { formData });
      const result = res.data?.body;
      await Promise.all([loadDetail(detail.id), loadList()]);
      modal.success({
        title: t('hotline_caseCreated'),
        content: (
          <div>
            <p className="text-[16px] font-semibold">{result?.caseNo}</p>
            <p>{t('hotline_caseCreatedDesc')}</p>
          </div>
        ),
        okText: t('hotline_openCase'),
        onOk: () => navigate(`${PageEnum.CASE_MANAGDETAL}/${result?.caseId}`),
        okCancel: true,
        cancelText: t('hotline_stay'),
        closable: true,
        maskClosable: true,
      });
    } catch (error: unknown) {
      message.error(String((error as { detail?: string })?.detail || error));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDismiss = () => {
    if (!detail) return;
    dismissNote.current = '';
    modal.confirm({
      title: t('hotline_dismissConfirm'),
      content: (
        <Input.TextArea
          rows={3}
          placeholder={t('hotline_dismissNote')}
          onChange={(e) => {
            dismissNote.current = e.target.value;
          }}
        />
      ),
      okText: t('hotline_dismiss'),
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await dismissAiCall(detail.id, dismissNote.current);
          message.success(t('success'));
          await Promise.all([loadDetail(detail.id), loadList()]);
        } catch (error: unknown) {
          message.error(String((error as { detail?: string })?.detail || error));
        }
      },
    });
  };

  const middle = useMemo(() => {
    if (!detail) {
      return (
        <div className="h-full flex items-center justify-center">
          <Empty
            description={detailLoading ? t('hotline_loading') : t('hotline_selectCall')}
          />
        </div>
      );
    }
    if (detail.formType !== 'BULKY_ITEM_REMOVAL') {
      return (
        <div className="max-w-[760px] mx-auto bg-[#fff] dark:bg-[#141414] rounded-xl p-6">
          <h2 className="text-[18px] font-semibold mb-2">{detail.summaryTitle}</h2>
          <p className="whitespace-pre-wrap">{detail.summary}</p>
          <pre className="mt-4 text-[12px] bg-[#f5f5f5] dark:bg-[#1f1f1f] p-3 rounded overflow-auto">
            {JSON.stringify(detail.formData, null, 2)}
          </pre>
          {detail.status === 'PENDING_REVIEW' && (
            <Button className="mt-4" danger onClick={handleDismiss}>
              {t('hotline_dismiss')}
            </Button>
          )}
        </div>
      );
    }
    return (
      <BulkyRemovalForm
        call={detail}
        submitting={submitting}
        onSubmit={handleSubmit}
        onDismiss={handleDismiss}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail, detailLoading, submitting, t]);

  return (
    <div className="flex h-[calc(100vh-60px)] overflow-hidden">
      <div className="w-[310px] shrink-0 h-full">
        <CallList
          calls={calls}
          activeId={detail?.id ?? selectedId}
          tab={tab}
          pendingCount={pendingCount}
          loading={listLoading}
          onTabChange={onTabChange}
          onSelect={select}
        />
      </div>
      <div className="flex-1 min-w-0 h-full overflow-y-auto bg-[#f4f7fc] dark:bg-[#000] p-4">
        {middle}
      </div>
      <div className="w-[350px] shrink-0 h-full">
        <CallTranscript
          call={detail}
          onTakeover={(next) => {
            setDetail(next);
            loadList();
          }}
          takeoverTarget={takeoverTarget}
          browserPhone={browserPhone}
        />
      </div>
      <BrowserCallPanel phone={browserPhone} />
    </div>
  );
}
